<template>
  <div class="managed-instruction-details pa-2 pb-10">
    <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs" />
    <div class="d-flex mb-2 ml-0">
      <v-btn
        v-if="!isReadOnlyUser"
        color="primary mr-4"
        @click.stop="openTagAssignmentDialog()"
      >
        {{ $t("managedInstructions.tagAssignmentDialog.open") }}
      </v-btn>
      <v-btn
        color="secondary"
        outlined
        class="mr-4"
        @click.stop="openPreview()"
      >
        {{ $t("managedInstructions.instruction.preview") }}
      </v-btn>
      <v-btn
        class="mr-2"
        color="primary"
        @click.stop="openEditDialog()"
        v-if="(isOwner || isLocal === 'local') && !isReadOnlyUser"
      >
        {{ $t("managedInstructions.instruction.editInstructionOwner") }}
      </v-btn>
      <v-btn
        v-if="isLocal === 'global' && !isReadOnlyUser"
        color="primary"
        outlined
        @click.stop="openLocalEditDialog()"
      >
        {{ $t("managedInstructions.instruction.editInstruction") }}
      </v-btn>
    </div>
    <v-card outlined class="mb-3">
      <v-card-title class="py-3">
        <span>{{ $t("managedInstructions.instruction.assignedTags") }}</span>
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="grey--text text--darken-1">
          {{ $t("managedInstructions.instruction.loadingTags") }}
        </div>
        <div v-else class="d-flex flex-wrap" style="gap: 8px">
          <v-chip
            v-for="t in assignedTags"
            :key="t._id"
            small
            label
            class="mr-1"
          >
            {{ t.tag }}
          </v-chip>
          <div v-if="assignedTags.length === 0" class="grey--text">
            {{ $t("managedInstructions.instruction.noTags") }}
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-row dense class="mb-1">
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-account-group</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.assignedEmployees") }}
              </div>
              <div class="text-h6">{{ eligibleCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-progress-clock</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.notCompleted") }}
              </div>
              <div class="text-h6">{{ inProgressCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-alert-circle</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.overdue") }}
              </div>
              <div class="text-h6">{{ overdueCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <div class="d-flex align-center justify-space-between mt-2">
      <v-tabs v-model="tab" background-color="transparent" style="flex: 1">
        <v-tab>
          {{ $t("managedInstructions.instruction.allEmployees") }}
        </v-tab>
        <v-tab v-if="hasExternals">
          {{ $t("managedInstructions.instruction.externals") }}
        </v-tab>
        <v-tab>
          {{ $t("managedInstructions.instruction.overdue") }}
        </v-tab>
      </v-tabs>

      <v-btn
        v-if="!isReadOnlyUser"
        color="secondary"
        outlined
        class="ml-4"
        @click.stop="sendReminderToAll()"
      >
        {{ $t("managedInstructions.instruction.sendReminders") }}
      </v-btn>
    </div>
    <v-tabs-items v-model="tab" class="mt-3">
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-text-field
              v-model="searchEligible"
              single-line
              hide-details
              dense
              clearable
              :label="$t('general.search')"
              style="max-width: 370px"
            />
          </v-card-title>
          <v-data-table
            :headers="employeeHeaders"
            :items="mainEmployeesEnriched"
            :search="searchEligible"
            :loading="loading"
            item-key="_id"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :class="statusChipClass(item.__assignmentStatus)"
              >
                {{ statusLabel(item.__assignmentStatus) }}
              </v-chip>
            </template>
            <template v-slot:item.dueDate="{ item }">
              <span v-if="item.__dueDate">
                {{ formatDate(item.__dueDate) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.overdue="{ item }">
              <v-icon v-if="item.__isOverdue" small>mdi-alert</v-icon>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="!isReadOnlyUser"
                icon
                small
                color="primary"
                @click.stop="sendReminder(item)"
              >
                <v-icon small>mdi-send-clock</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>{{
              $t("managedInstructions.instruction.noEmployeesFound")
            }}</template>
          </v-data-table>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="hasExternals">
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-text-field
              v-model="searchExternal"
              single-line
              hide-details
              dense
              clearable
              :label="$t('general.search')"
              style="max-width: 370px"
            />
          </v-card-title>
          <v-data-table
            :headers="employeeHeaders"
            :items="externalEmployeesEnriched"
            :search="searchExternal"
            :loading="loading"
            item-key="_id"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :class="statusChipClass(item.__assignmentStatus)"
              >
                {{ statusLabel(item.__assignmentStatus) }}
              </v-chip>
            </template>
            <template v-slot:item.dueDate="{ item }">
              <span v-if="item.__dueDate">
                {{ formatDate(item.__dueDate) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.overdue="{ item }">
              <v-icon v-if="item.__isOverdue" small>mdi-alert</v-icon>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="!isReadOnlyUser"
                icon
                small
                color="primary"
                @click.stop="sendReminder(item)"
              >
                <v-icon small>mdi-send-clock</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>{{
              $t("managedInstructions.instruction.noEmployeesFound")
            }}</template>
          </v-data-table>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-text-field
              v-model="searchOverdue"
              single-line
              hide-details
              dense
              clearable
              :label="$t('general.search')"
              style="max-width: 370px"
            />
          </v-card-title>
          <v-data-table
            :headers="employeeHeaders"
            :items="overdueEmployeesEnriched"
            :search="searchOverdue"
            :loading="loading"
            item-key="_id"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :class="statusChipClass(item.__assignmentStatus)"
              >
                {{ statusLabel(item.__assignmentStatus) }}
              </v-chip>
            </template>
            <template v-slot:item.dueDate="{ item }">
              <span v-if="item.__dueDate">
                {{ formatDate(item.__dueDate) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.overdue="{ item }">
              <v-icon v-if="item.__isOverdue" small>mdi-alert</v-icon>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="!isReadOnlyUser"
                icon
                small
                color="primary"
                @click.stop="sendReminder(item)"
              >
                <v-icon small>mdi-send-clock</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              {{
                $t("managedInstructions.instruction.noOverdueEmployees")
              }}</template
            >
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <managed-instruction-tag-assignment-dialog
      ref="tagAssignmentDialog"
      :instruction-id="instructionId"
      @saved="loadAll()"
    />
    <managed-instruction-edit-dialog
      ref="editDialog"
      :instruction-id="instructionId"
      @saved="loadAll()"
    />

    <managed-instruction-local-edit-dialog
      ref="editLocalDialog"
      :instruction-id="instructionId"
      @saved="loadAll()"
    />
  </div>
</template>
<script>
import { isMaster } from "../../utils/masterEmails";
import i18n from "../../i18n";
import ManagedInstructionTagAssignmentDialog from "@/views/managedInstruction/managedInstructionTagAssignmentDialog.vue";
import ManagedInstructionEditDialog from "@/views/managedInstruction/managedInstructionEditDialog.vue";
import ManagedInstructionLocalEditDialog from "@/views/managedInstruction/managedInstructionLocalEditDialog.vue";

export default {
  name: "ManagedInstructionDetailsPage",
  components: {
    ManagedInstructionTagAssignmentDialog,
    ManagedInstructionEditDialog,
    ManagedInstructionLocalEditDialog,
  },
  props: { instructionId: { type: String, required: true } },
  data: () => ({
    isOwner: false,
    isLocal: false,
    title: "",
    tab: 0,
    loading: false,
    searchEligible: "",
    searchOverdue: "",
    searchExternal: "",
    instruction: null,
    assignedTags: [],
    eligibleEmployees: [],
    employeeAssignments: [],
    assignment: null,
    stats: {
      eligibleCount: 0,
      inProgressCount: 0,
      overdueCount: 0,
    },
  }),
  computed: {
    breadcrumbs() {
      return [
        {
          text: i18n.t("sidebar.onlineInstructions"),
          disabled: false,
          to: { path: "/app/instructionsHub", query: { tab: "managed" } },
          exact: true,
        },
        {
          text: this.title || this.instruction?.name || "Details",
          disabled: true,
        },
      ];
    },
    employeeHeaders() {
      return [
        {
          text: this.$t("managedInstructions.tableHeader.name"),
          value: "name",
        },
        {
          text: this.$t("managedInstructions.tableHeader.email"),
          value: "email",
        },
        {
          text: this.$t("managedInstructions.tableHeader.status"),
          value: "status",
          align: "center",
        },
        {
          text: this.$t("managedInstructions.tableHeader.overdue"),
          value: "overdue",
          align: "center",
          sortable: false,
        },
        {
          text: "",
          value: "actions",
          align: "end",
          sortable: false,
          width: 60,
        },
      ];
    },
    assignmentByEmployeeId() {
      const map = new Map();
      for (const a of this.employeeAssignments) {
        const employeeId =
          typeof a.employee === "string" ? a.employee : a.employee?._id;
        if (employeeId) map.set(employeeId, a);
      }
      return map;
    },
    eligibleEmployeesEnriched() {
      const now = new Date();

      return this.eligibleEmployees.map((e) => {
        const id = e._id;
        const a = this.assignmentByEmployeeId.get(id);

        const status = a?.status || null;
        const dueDate = a?.dueDate ? new Date(a.dueDate) : null;
        const overdueDate = a?.overdueDate ? new Date(a.overdueDate) : null;

        const isDone = status === "done";
        const isOverdue =
          !!overdueDate && !isDone && overdueDate.getTime() <= now.getTime();

        return {
          ...e,
          __assignmentStatus: status,
          __dueDate: dueDate,
          __overdueDate: overdueDate,
          __isOverdue: isOverdue,
        };
      });
    },
    mainEmployeesEnriched() {
      return this.eligibleEmployeesEnriched.filter((e) => !e.external);
    },
    externalEmployeesEnriched() {
      return this.eligibleEmployeesEnriched.filter((e) => e.external);
    },
    hasExternals() {
      return this.externalEmployeesEnriched.length > 0;
    },
    overdueEmployeesEnriched() {
      return this.eligibleEmployeesEnriched.filter(
        (e) => !e.external && e.__isOverdue,
      );
    },
    eligibleCount() {
      return this.eligibleEmployees.length;
    },
    inProgressCount() {
      return this.eligibleEmployeesEnriched.filter(
        (e) => !!e.__assignmentStatus && e.__assignmentStatus !== "done",
      ).length;
    },
    overdueCount() {
      return this.overdueEmployeesEnriched.length;
    },
  },
  created() {
    this.loadAll();
    this.loadUserRole();
  },
  mounted() {
    this.title = this.$route.params.instructionName;
  },
  methods: {
    async loadUserRole() {
      try {
        const user = await this.$store.dispatch("getUserInfo");
        this.isOwner = user && isMaster(user.email);
      } catch (e) {
        this.isOwner = false;
      }
    },

    openEditDialog() {
      this.$nextTick(() => this.$refs.editDialog.openDialog());
    },

    openLocalEditDialog() {
      this.$nextTick(() => this.$refs.editLocalDialog.openDialog());
    },
    async loadAll() {
      this.loading = true;
      try {
        const { data } = await this.$store.dispatch(
          "getAssignmentOverview",
          this.instructionId,
        );

        this.instruction = data.instruction || null;
        this.isLocal = data.instruction.type;
        this.assignment = data?.assignment || null;

        this.assignedTags = data.assignedTags || [];
        this.stats = data.stats || {
          eligibleCount: 0,
          inProgressCount: 0,
          overdueCount: 0,
        };

        this.eligibleEmployees = (data.employees || []).map((e) => ({
          _id: e._id,
          name: e.name,
          email: e.email,
          external: !!e.external,
        }));

        this.employeeAssignments = (data.employees || [])
          .filter((e) => !!e.assignment)
          .map((e) => ({
            _id: e.assignment._id,
            employee: e._id,
            status: e.assignment.status,
            dueDate: e.assignment.dueDate,
            overdueDate: e.assignment.overdueDate,
            createdAt: e.assignment.createdAt,
          }));
      } finally {
        this.loading = false;
      }
    },
    openTagAssignmentDialog() {
      this.$nextTick(() => this.$refs.tagAssignmentDialog.openDialog());
    },
    openPreview() {
      const route = this.$router.resolve({
        name: "managedInstructionPreview",
        params: { instructionId: this.instructionId },
        query: { type: this.isLocal || "" },
      });
      window.open(route.href, "_blank", "noopener");
    },
    statusLabel(status) {
      if (!status) return "not assigned";
      if (status === "assigned") return "assigned";
      if (status === "open") return "open";
      if (status === "done") return "done";
      return status;
    },
    statusChipClass(status) {
      if (status === "done") return "green lighten-4";
      if (status === "open") return "blue lighten-4";
      if (status === "assigned") return "grey lighten-3";
      return "grey lighten-4";
    },
    formatDate(d) {
      if (!d) return "";
      const date = d instanceof Date ? d : new Date(d);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },
    async sendReminder(item) {
      const res = this.employeeAssignments.find(
        (employee) => employee.employee === item._id,
      );

      await this.$store.dispatch("sendSingleManagedInstructionReminder", {
        _id: item._id,
        instructionId: res._id,
      });

      await this.loadAll();
    },

    async sendReminderToAll() {
      await this.$store.dispatch("sendAllManagedInstructionReminder", {
        instructionId: this.assignment._id,
      });

      await this.loadAll();
    },
  },
};
</script>
<style>
.managed-instruction-details .v-tab {
  text-transform: none;
}
</style>
